import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const HolidayView = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 15,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(new Date().getFullYear()); // Default to current year
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, search, year, sortBy, sortOrder]);

  const fetchHolidays = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'list-holiday',
        currentUserId: currentUserId,
        year: year,
        ...(search && { search: search }),
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(sortBy && { sortBy: sortBy, sortOrder: sortOrder })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setHolidays(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch holidays');
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Handle format like "26-01-2026"
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = parseInt(month) - 1;
          return `${day}-${monthNames[monthIndex]}-${year}`;
        }
      }
      // Handle ISO date format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                View Holidays
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View all holidays for your organization.
              </p>
            </div>
          </div>

          {/* Holiday Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '200px' }}>
                    <input
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search holidays..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                    <label htmlFor="year" className="form-label" style={{ marginBottom: '5px', fontSize: '12px', color: '#6b7280', display: 'block' }}>
                      Year
                    </label>
                    <div className="date-input-wrapper" style={{ position: 'relative' }}>
                      <input
                        type="number"
                        id="year"
                        className="form-input"
                        placeholder="Select Year"
                        value={year}
                        onChange={(e) => {
                          const yearValue = parseInt(e.target.value, 10);
                          const currentYear = new Date().getFullYear();
                          const previousYear = currentYear - 1;
                          // Allow current year and previous year only (no future years) for all users
                          if (!isNaN(yearValue) && yearValue >= previousYear && yearValue <= currentYear) {
                            setYear(yearValue);
                            setPagination(prev => ({ ...prev, currentPage: 1 }));
                          } else if (e.target.value === '') {
                            setYear(currentYear);
                          }
                        }}
                        min={new Date().getFullYear() - 1}
                        max={new Date().getFullYear()}
                        style={{ 
                          padding: '8px 35px 8px 12px', 
                          fontSize: '14px',
                          height: '40px',
                          width: '150px'
                        }}
                      />
                      <i className="fas fa-calendar-alt date-icon" style={{ 
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        pointerEvents: 'none',
                        fontSize: '14px'
                      }}></i>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th className="employee-sortable">S.No</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('holiday')}
                            >
                              Holidays
                              {sortBy === 'holiday' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'holiday' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('date')}
                            >
                              Date
                              {sortBy === 'date' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'date' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('day')}
                            >
                              Day
                              {sortBy === 'day' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'day' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {holidays.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="employee-empty">
                                No holidays found
                              </td>
                            </tr>
                          ) : (
                            holidays.map((holiday, index) => {
                              return (
                                <tr key={holiday._id}>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{holiday.holiday || '-'}</td>
                                  <td>{formatDate(holiday.date)}</td>
                                  <td>{holiday.day || '-'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {holidays.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.currentPage === 1}
                        >
                          «
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                        >
                          ‹
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          ›
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          »
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HolidayView;
